import * as APIM from '@common/utils/dist/models/api-messages.js';
import * as PL2 from '@common/utils/dist/models/models-pl2.js';

import {TrialDataWindow} from '@models/trial-data-window-types';
import {
  buildTrialDataWindow,
  recordedDataConfigState,
} from '@utils/trial-data-window-builder';

/**
 * Builders for the chat's view of what the user is attending to: one per kind of lesson item
 * (see APIM.AgentAttentionKind). Live data and saved trials so far.
 */

/** The attention kind of a lesson card, or undefined for cards the chat can't attend to. */
export function attentionKind(
  entryType: PL2.EntryType | string | undefined,
): APIM.AgentAttentionKind | undefined {
  switch (entryType) {
    case PL2.EntryType.CollectDataSection:
      return 'live-data';
    case PL2.EntryType.TrialSection:
      return 'trial';
    default:
      return undefined;
  }
}

export interface CardDataWindowParams {
  cardPK: string;
  card: Partial<PL2.AnyEntry>;
  /** The live data, and the data card it's shown in (the active connection data view) */
  liveDataConfigState: PL2.DataConfigState;
  liveCardPK: string | null;
  /** A saved trial card's plots (see LoadTrialPlotsAction) */
  trialPlots?: PL2.Plot[];
  deviceName: string;
}

/**
 * The data of a focused card: the live data when it's the data card showing the connection
 * (or no card is), or a saved trial's recorded plots. Null when it has none.
 */
export function cardDataWindow(
  params: CardDataWindowParams,
): TrialDataWindow | null {
  const {cardPK, card, liveDataConfigState, liveCardPK, trialPlots} = params;
  let dataConfigState: PL2.DataConfigState | undefined;
  switch (attentionKind(card.t)) {
    case 'trial':
      dataConfigState = trialPlots?.length
        ? recordedDataConfigState(trialPlots)
        : undefined;
      break;
    case 'live-data':
      dataConfigState =
        liveCardPK === cardPK || liveCardPK === null
          ? liveDataConfigState
          : undefined;
      break;
  }
  if (!dataConfigState) return null;
  return buildTrialDataWindow({
    dataConfigState,
    dataZoomState: {minMaxIndex: {}},
    trialPK: cardPK,
    trialName: card.n ?? '',
    cardPK,
    deviceName: params.deviceName,
  });
}
